import { Action, Dispatch } from "redux"
import { mapListToCamelCase, mapToCamelCase } from "../utility"
import { handleAuthorizedRequest, trends } from "../apis"
import { lang } from "../constants/languages"
import { TrendFilter } from "../types"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

export const TRENDS_ACTION = {
  FETCH_FILTER_OPTIONS: "@TREND/FETCH_FILTER_OPTIONS",
  FETCH_STREAMS_DAILY: "@TREND/FETCH_STREAMS_DAILY",
  FETCH_STREAMS_COUNTRY: "@TREND/FETCH_STREAMS_COUNTRY",
  FETCH_STREAMS_DEMOGRAPHIC: "@TREND/FETCH_STREAMS_DEMOGRAPHIC",
  FETCH_TREND_TOP_TRACKS: "@TREND/FETCH_TREND_TOP_TRACKS",
  FETCH_STREAMS_TRACK_MONTHS: "@TREND/FETCH_STREAMS_TRACK_MONTHS",
  FETCH_ARTIST_TREND_ECONOMIES: "@TREND/FETCH_ARTIST_TREND_ECONOMIES"
}

const formatDate = (date?: Date) => {
  if (!date) {
    return ""
  }

  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}${month}${day}`
}

const buildParams = (filter: TrendFilter, addEventType?: boolean) => {
  const start = filter.dateValues ? formatDate(filter.dateValues[0]) : ""
  const end = filter.dateValues ? formatDate(filter.dateValues[1]) : ""
  const isrcParam = filter.isrcList?.length > 0 ? `&isrc_list=${filter.isrcList.join(",")}` : ""
  const countryParam = filter.countryList?.length > 0 ? `&country_list=${filter.countryList.join(",")}` : ""
  const sexParam = filter.sexList?.length > 0 ? `&sex_list=${filter.sexList.join(",")}` : ""
  const ageGroupParam = filter.ageGroupList?.length > 0 ? `&age_group_list=${filter.ageGroupList.join(",")}` : ""
  const platformListParam = filter.platformList?.length > 0 ? `&platform_list=${filter.platformList.join(",")}` : ""
  const artistListParam = filter.artistList?.length > 0 ? `&artist_list=${filter.artistList.join(",")}` : ""
  const userIdsParam = (filter.selectedUsers || []).length > 0 ? `&user_ids=${filter.selectedUsers?.join(",")}` : ""
  const isAdminParam = filter.isAdmin ? `&is_admin=1` : ""
  const eventTypeParam =
    filter.eventType === "social"
      ? "&event_type=social_production,social_consumption"
      : `&event_type=${filter.eventType}`

  return `?start_date=${start}&end_date=${end}${isrcParam}${countryParam}${sexParam}${ageGroupParam}${artistListParam}${userIdsParam}${platformListParam}${isAdminParam}${addEventType ? eventTypeParam : ""}`
}

export const fetchStreamsDaily = (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await trends(`/streams_daily/${buildParams(filter, true)}`, "get")

      dispatch({
        type: TRENDS_ACTION.FETCH_STREAMS_DAILY,
        payload: mapListToCamelCase(response.data.results)
      })

      if (fetchOptions) {
        dispatch({
          type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
          payload: mapToCamelCase(response.data.options)
        })
      }
    },
    lang.trends.error_on_streams_daily,
    dispatch
  )
}

export const fetchStreamsCountry =
  (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await trends(`/streams_country/${buildParams(filter, true)}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_COUNTRY,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      lang.trends.error_on_streams_country,
      dispatch
    )
  }

export const fetchStreamsDemographic =
  (filter: TrendFilter, fetchOptions: boolean) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await trends(`/streams_demographic/${buildParams(filter, true)}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_DEMOGRAPHIC,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      lang.trends.error_on_streams_demographic,
      dispatch
    )
  }

export const fetchTrendTopTracks =
  (date: string, fetchOptions: boolean, filter: TrendFilter) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const platformFilter = filter.platformList ? `&platform_list=${filter.platformList.join(",")}` : ""
        const response = await trends(`/top_trends/?date=${date}${platformFilter}`, "get")

        dispatch({
          type: TRENDS_ACTION.FETCH_TREND_TOP_TRACKS,
          payload: mapListToCamelCase(response.data.results)
        })

        if (fetchOptions) {
          dispatch({
            type: TRENDS_ACTION.FETCH_FILTER_OPTIONS,
            payload: mapToCamelCase(response.data.options)
          })
        }
      },
      "Error fetching trend top tracks",
      dispatch
    )
  }

export const fetchStreamsTrackMonths =
  (filter: TrendFilter, offset: number, limit: number, selectedRoyaltyType?: KSelectOption) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const royaltyTypeParam = selectedRoyaltyType ? `&royalty_type=${selectedRoyaltyType.value2}` : ""
        const response = await trends(
          `/streams_track_months/${buildParams(filter)}&offset=${offset}&limit=${limit}${royaltyTypeParam}`,
          "get"
        )

        dispatch({
          type: TRENDS_ACTION.FETCH_STREAMS_TRACK_MONTHS,
          payload: {
            totals: mapListToCamelCase(response.data.totals),
            results: mapListToCamelCase(response.data.results),
            chartData: mapListToCamelCase(response.data.chart_data),
            totalStreams: response.data.total_streams,
            missingDays: response.data.missing_days || []
          }
        })
      },
      "Error fetching streams track months",
      dispatch
    )
  }

export const fetchArtistTrendEconomyData =
  (filter: TrendFilter, offset: number, limit: number, selectedRoyaltyType?: KSelectOption) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const royaltyTypeParam = selectedRoyaltyType ? `&royalty_type=${selectedRoyaltyType.value2}` : ""
        const response = await trends(
          `/artist_trend_economy/${buildParams(filter)}&offset=${offset}&limit=${limit}${royaltyTypeParam}`,
          "get"
        )

        dispatch({
          type: TRENDS_ACTION.FETCH_ARTIST_TREND_ECONOMIES,
          payload: {
            totals: mapListToCamelCase(response.data.totals),
            results: mapListToCamelCase(response.data.results),
            chartData: mapListToCamelCase(response.data.chart_data),
            totalRoyalty: response.data.total_royalty
          }
        })
      },
      "Error fetching artist trend economies",
      dispatch
    )
  }
